import { Component, Ref, Prop, SimpleEvent } from "@eastsideco/salvo-ts";
import { ThemeComponent } from "@/theme/ThemeComponent";
import { SubscriptionForm } from '../SubscriptionForm/SubscriptionForm';
import { StickyFooterBar } from "../StickyFooterBar/StickyFooterBar";
import { DropdownButton } from "../DropdownButton/DropdownButton";

/**
 * Linked Subscriptions
 *
 * This component exclusively handles recharge subscription options on
 * product pages.
 *
 * Functionality around checking URL params is included, but
 * could potentially be moved in to liquid so as not to switch
 * the default selected recharge option after page load
 *
 */

type SelectedOptionData = {
    sellingPlan: string;
    absoluterSellingPlan: string;
    frequency: string;
    frequencyUnit: string;
    quantity: string;
    price: string;
    absoluterPrice: string;
    hasGift: string;
    giftIds: string;
};
@Component
export class LinkedSubscriptions extends ThemeComponent {

    @Prop({ default: false }) isAbsoluter!: boolean;
    @Ref() radioInput!: HTMLInputElement[];
    @Ref({ required: false }) flavourRadio!: HTMLInputElement[];

    private productMain: HTMLElement | null = null;
    private selectedRadioInput: HTMLInputElement|null = null;
    private subscriptionForm: SubscriptionForm|null = null;
    private submitButton: HTMLInputElement[]|null = [];
    private stickyFooterBar: StickyFooterBar|null = null;
    private stickyFooterBarDropdown: DropdownButton|null = null;

    private _onRadioChange = new SimpleEvent<string>();
    public get onRadioChange() {
        return this._onRadioChange.asEvent();
    }

    async init() {

        // Assign the main pdp wrapper
        this.productMain = document.querySelector('.product-main__wrapper');

        // Assign the wrapping subscription form
        this.subscriptionForm = this.closest('subscription-form') as SubscriptionForm;

        // Assign the sticky footer bar
        this.stickyFooterBar = document.querySelector('sticky-footer-bar') as StickyFooterBar;

        // Assign the sticky footer bar dropdown
        this.stickyFooterBarDropdown = this.stickyFooterBar?.querySelector('dropdown-button') as DropdownButton;

        // Assign the submit button
        if (this.subscriptionForm) {
            this.submitButton = [
                ...this.subscriptionForm.querySelectorAll('[data-ref="submitButton"]')
            ] as HTMLInputElement[];
        }

        // Return if no subscription form is found
        if (!this.subscriptionForm) {
            return;
        }

        // Set the selected radio input
        this.selectedRadioInput = this.radioInput.find((input) => input.checked) || null;

        // Update the frequency label
        // this.updateFrequencyLabel();

        // Listen for radio input changes
        if (this.subscriptionForm && this.radioInput) {
            for (const radio of this.radioInput) {
                this.$listen(radio, 'change', async(event: Event) => {
                    await this.updateSelectedOption(radio.id);
                });
            }
        }

        // Subscribe to dropdown selections on the sticky footer bar
        if (this.stickyFooterBarDropdown) {
            this.$subscribe(this.stickyFooterBarDropdown.onDropDownSelect, async(id: string) => {
                const radioId = id.replace('option-', '');
                if (this.selectedRadioInput?.id == `${radioId}`) {
                    return;
                } else {
                    this.selectRadioOption(radioId);
                }
                await this.updateSelectedOption(radioId);
            });
        }

        // Handle charge delay
        if (this.selectedRadioInput) {
            await this.handleChargeDelay(this.subscriptionForm, this.selectedRadioInput);
        }

        // window.addEventListener('resize', debounce(this.updateFrequencyLabel.bind(this), 300));

    }

    // Select radio option by id
    selectRadioOption(radioId: string) {
        const radioToSelect = this.radioInput.find((input) => input.id == radioId) || null;
        if (radioToSelect) {
            radioToSelect.checked = true;
        }
    }


    public async updateSelectedOption(id: string) {
        this.selectedRadioInput = this.radioInput.find((input) => input.id == id) || null;
        if (this.selectedRadioInput) {
            await this.handleRadioChange();
        }
    }

    // Set gift ids
    setGift(giftCheck:string, giftIds:string) {
        if (this.submitButton) {
            for (const button of this.submitButton) {
                button.setAttribute('has_gift', giftCheck);

                if (giftCheck === 'true') {
                    button.setAttribute('gift_ids', giftIds);
                } else {
                    button.removeAttribute('gift_ids');
                }
            }
        }
    }

    async handleRadioChange() {

        // this.updateFrequencyLabel();

        // Set selected radio label
        this.setSelectedRadioLabel();

        // TODO Handle charge delay?
        // await this.handleChargeDelay(
        //     this.subscriptionForm as HTMLElement,
        //     this.selectedRadioInput as HTMLInputElement
        // );

        // Dispatch event
        if (this.selectedRadioInput) {
            this._onRadioChange.dispatch(this.selectedRadioInput.id);
        }
    }

    // Set selected radio appearance
    setSelectedRadioLabel() {
        // Deselect all radios and select the applicable radio
        for (const radio of this.radioInput) {
            const radioParent = radio.closest('.radio-selection');
            if (radio == this.selectedRadioInput) {
                radioParent?.classList.add('radio-selection--checked');
            } else {
                radioParent?.classList.remove('radio-selection--checked');
            }
        }
    }

    // Handle the charge delay
    async handleChargeDelay(parentElement: HTMLElement, option: HTMLInputElement) {

        try {
            const chargeFrequencyValue = option.getAttribute('data-frequency') as string;
            const chargeFrequencyInput = parentElement.querySelector('#chargeDelay') as HTMLInputElement;
            const chargeDelayExperimentInput =
                parentElement.querySelector('#charge_delay_experiment') as HTMLInputElement;

            if (
                (!chargeFrequencyValue || chargeFrequencyValue == '0') &&
                chargeFrequencyInput
            ) {
                chargeFrequencyInput.value = '0';
                return;
            }
            let chargeDelay: number = 54;
            if (
                chargeFrequencyInput &&
                this.submitButton
            ) {
                chargeFrequencyInput.disabled = false;
                if (chargeFrequencyValue == '10' ) {
                    chargeDelay = 68;
                } else if (
                    chargeFrequencyValue != '8' &&
                    chargeFrequencyValue != '6'
                ) {
                    chargeDelay = Number(chargeFrequencyValue) - 2;
                } else if (chargeFrequencyValue == '6') {
                    chargeDelay = 40;
                } else {
                    chargeDelay = 54;
                }
                /*
                    Charge Delay Experiemnt - Applies to two products only
                        - Marine Liquid Collagen Drink For Women, 14x10ml Sachets
                        - Marine Collagen Protein Supplement For Men, 14x10ml sachets
                */
                const isAbTestActive = this.productMain !== null
                    && this.productMain.hasAttribute('data-charge-delay-experiment-active');
                if (isAbTestActive) {
                    const chargeDelayExperimentEnabled = true;
                    if (chargeDelayExperimentEnabled) {
                        chargeDelay =
                        await this.chargeDelayExperiment(chargeDelayExperimentInput,
                            chargeDelay) || chargeDelay;
                    }
                }
                chargeFrequencyInput.value = chargeDelay.toString();
                if (this.submitButton) {
                    for (const button of this.submitButton) {
                        button.disabled = false;
                    }
                }
            }
        } catch (error) {
            throw new Error();
        }
    }

    async chargeDelayExperiment(chargeDelayExperimentInput: HTMLInputElement, chargeDelay: number) {
        const now = new Date();
        const dayOfWeek = now.getUTCDay();
        const hour = now.getHours();
        // Generate a random number between 1 - 10
        // If number is even then they will be included in the experiment
        const randomValue = Math.floor(Math.random() * 10) + 1;
        const partOfExperiment = randomValue % 2 === 0 ? true : false;
        // Check for weekdays and if before/after 16:00 pm.
        // If 4pm or later then it will add another day to chargeDelay
        if (dayOfWeek >= 1 && dayOfWeek <= 5 && hour >= 16 && partOfExperiment) {
            chargeDelay += 1;
        }
        chargeDelayExperimentInput.value = partOfExperiment.toString();
        if (this.submitButton) {
            for (const button of this.submitButton) {
                button.setAttribute('_charge_delay_experiment', partOfExperiment.toString());
            }
        }
        return chargeDelay;
    }

    updateFrequencyLabel() {
        const checkedFreq =
            Array.from(document.querySelectorAll<HTMLInputElement>(
                '.radio-selection .product-form__subscription-choice'
            )).find((input) => input.checked);

        if (checkedFreq) {
            const labelText = checkedFreq.dataset.label || '';
            const stickyFrequencyText =
            document.querySelector<HTMLSpanElement>('.product-sticky-footer__selection .subscription-plan');

            if (stickyFrequencyText) {
                stickyFrequencyText.textContent = window.innerWidth < 1024 ? `${labelText}` : `, ${labelText}`;
            }
        }
    }

}
